import { TripType, CepData, BookingFlow } from '@flysas-tech/book-value-stream-models';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { DateRange, dateTimeFrom, isInRange, PassengerErrorType, validatePassengerCounts } from 'shared';

enum ErrorType {
  FIELD_REQUIRED = 'Field Required',
  FIELD_INVALID = 'Invalid',
}

export type FormErrors = {
  origin?: ErrorType;
  destination?: ErrorType;
  outboundDate?: ErrorType;
  inboundDate?: ErrorType;
  passengerCounts?: ErrorType | PassengerErrorType;
  formError?: string;
};

const cepErrorMessages: Record<string, string> = {
  [ErrorType.FIELD_REQUIRED]: t('common:form.fieldRequired'),
  [ErrorType.FIELD_INVALID]: 'Invalid',
  [PassengerErrorType.MAX_NINE_ADULTS_AND_CHILDREN]: 'Invalid passenger count',
  [PassengerErrorType.INFANT_EXCEEDS_ADULT]: 'Invalid passenger count',
  [PassengerErrorType.AT_LEAST_ONE_YOUNG]: 'Invalid passenger count',
  [PassengerErrorType.MAX_NINE_YOUNG]: 'Invalid passenger count',
};

export const getErrorMessage = (errorType: ErrorType | PassengerErrorType | undefined) => {
  if (!errorType) {
    return undefined;
  }

  return cepErrorMessages[errorType] ?? errorType;
};

export function validateLocation(location: string) {
  if (!location) {
    return ErrorType.FIELD_REQUIRED;
  }

  if (location.length !== 3) {
    return ErrorType.FIELD_INVALID;
  }
}

export function getBookingBoundary(bookingFlow: BookingFlow): DateRange {
  const now = DateTime.local();
  let from = now.startOf('day');
  const to = now.plus({ years: 1 }).endOf('month').endOf('day');

  if (bookingFlow === BookingFlow.AWARD) {
    from = from.plus({ days: 4 });
  }

  return { from, to };
}

function getFormattedBoundDate(date: unknown, range: DateRange): string | undefined {
  const _date = typeof date === 'string' || date instanceof DateTime ? dateTimeFrom(date) : undefined;

  if (!_date?.isValid) return undefined;

  return isInRange({ range, date: _date }) ? _date.toFormat('yyyy-MM-dd') : undefined;
}

export function getFormattedBoundDates(
  outboundDate: unknown,
  inboundDate: unknown,
  range: { from: DateTime<true>; to: DateTime<true> }
): { outboundDate: string; inboundDate?: string } | undefined {
  const formattedOutboundDate = getFormattedBoundDate(outboundDate, range);

  if (!formattedOutboundDate) {
    return undefined;
  }

  if (!inboundDate) {
    return { outboundDate: formattedOutboundDate };
  }

  const formattedInboundDate = getFormattedBoundDate(inboundDate, range);

  if (!formattedInboundDate) {
    return undefined;
  }

  return formattedOutboundDate <= formattedInboundDate
    ? { outboundDate: formattedOutboundDate, inboundDate: formattedInboundDate }
    : undefined;
}

type Validation = { isValid: true; data: CepData } | { isValid: false; errors: FormErrors };

export function validateCepData(cepData: Partial<CepData>, bookingFlow: BookingFlow): Validation {
  let errors: FormErrors = {};

  const { origin, destination, tripType, outboundDate, inboundDate, passengerCounts } = cepData;

  if (!origin) {
    errors = { ...errors, origin: ErrorType.FIELD_REQUIRED };
  } else if (origin.length !== 3) {
    errors = { ...errors, origin: ErrorType.FIELD_INVALID };
  }

  if (!destination) {
    errors = { ...errors, destination: ErrorType.FIELD_REQUIRED };
  } else if (destination.length !== 3) {
    errors = { ...errors, destination: ErrorType.FIELD_INVALID };
  }

  if (!passengerCounts) {
    errors = { ...errors, passengerCounts: ErrorType.FIELD_REQUIRED };
  } else {
    const { adults, children, infants } = passengerCounts;
    const { isValid, errorMsg } = validatePassengerCounts({ adults, children, infants });

    if (!isValid) {
      errors = { ...errors, passengerCounts: errorMsg };
    }
  }

  if (!outboundDate) {
    errors = { ...errors, outboundDate: ErrorType.FIELD_REQUIRED };
  }

  if (tripType !== TripType.ONE_WAY && !inboundDate) {
    errors = { ...errors, inboundDate: ErrorType.FIELD_REQUIRED };
  }

  if (Object.keys(errors).length !== 0) {
    return { isValid: false, errors };
  }

  const range = getBookingBoundary(bookingFlow);

  const boundDates = getFormattedBoundDates(outboundDate, inboundDate, range);

  let boundsValid = Boolean(boundDates && boundDates.outboundDate);
  if (tripType !== TripType.ONE_WAY) {
    boundsValid = Boolean(boundsValid && boundDates?.inboundDate);
  }

  if (!boundDates || !boundsValid) {
    return { isValid: false, errors: { formError: 'Invalid Date Range' } };
  }

  const validatedCepData = {
    ...cepData,
    outboundDate: boundDates.outboundDate,
    inboundDate: boundDates.inboundDate,
  } as CepData;

  return {
    isValid: true,
    data: validatedCepData,
  };
}
